/** @jsxImportSource @emotion/react */
import { useEffect, useRef, useState } from 'react';

import type { ApiScan } from '@kaiizen/kai-pro-api';
import { useTaskModifierDisplayNames } from '@kaiizen/kai-pro-api-react-state';

import { CaretDownIcon } from '@/Client/Icons/kaiShikiIconAliases';
import { ModalShell } from '@/Client/Widgets';
import { Card, CommonActionButton, CommonFieldInput, CommonFieldLabel, CommonFieldShell, CommonToggle } from '@kaiizen/kai-shiki/react';
import { getHistoricalArtifactSubtitle } from './FocusedArtifactPanelLib';
import {
  CREATE_MODAL_CARD_STYLE,
  EYEBROW_STYLE,
  FIELD_GROUP_STYLE,
  FIELD_HELPER_STYLE,
  FIELD_INPUT_STYLE,
  FIELD_LABEL_STYLE,
  FIELD_SELECT_ICON_STYLE,
  FIELD_SELECT_STYLE,
  FIELD_SHELL_STYLE,
  MODAL_ACTIONS_STYLE,
  MODAL_FORM_STYLE,
  MODAL_SETTING_BODY_STYLE,
  MODAL_SETTING_COPY_STYLE,
  MODAL_SETTING_ROW_STYLE,
  MODAL_SETTING_TITLE_STYLE,
  MODAL_TITLE_STYLE,
  PRIMARY_ACTION_BUTTON_STYLE,
  SECONDARY_ACTION_BUTTON_STYLE,
  SECTION_DESCRIPTION_STYLE,
} from './FocusedArtifactPanelStyleLib';

/** A recipient-visibility option displayed while creating an audience-doc copy. */
const AudienceDocSettingToggle = ({
  ariaLabel,
  body,
  checked,
  onToggle,
  title,
}: {
  ariaLabel: string;
  body: string;
  checked: boolean;
  onToggle: () => void;
  title: string;
}) => (
  <div css={MODAL_SETTING_ROW_STYLE}>
    <div css={MODAL_SETTING_COPY_STYLE}>
      <div css={MODAL_SETTING_TITLE_STYLE}>{title}</div>
      <div css={MODAL_SETTING_BODY_STYLE}>{body}</div>
    </div>
    <CommonToggle checked={checked} aria-label={ariaLabel} onClick={onToggle} />
  </div>
);

/** Modal for minting a shareable audience-doc copy from a chosen approved or draft scope artifact. */
export const CreateAudienceDocModal = ({
  baseArtifacts,
  baseArtifactTitlesById,
  defaultBaseArtifactId,
  defaultName,
  isCreating,
  visible,
  onClose,
  onCreate,
}: {
  baseArtifacts: ApiScan[];
  baseArtifactTitlesById: Map<string, string>;
  defaultBaseArtifactId?: string;
  defaultName: string;
  isCreating: boolean;
  visible: boolean;
  onClose: () => void;
  onCreate: (payload: {
    includePhotos: boolean;
    name: string;
    parentScanId: string;
    photosOnly: boolean;
    residentResponsibleOnly: boolean;
    showAmounts: boolean;
  }) => Promise<void>;
}) => {
  const [includePhotos, setIncludePhotos] = useState(true);
  const [name, setName] = useState(defaultName);
  const [parentScanId, setParentScanId] = useState(defaultBaseArtifactId ?? baseArtifacts.at(-1)?.id ?? '');
  const [photosOnly, setPhotosOnly] = useState(false);
  const [residentResponsibleOnly, setResidentResponsibleOnly] = useState(false);
  const [showAmounts, setShowAmounts] = useState(true);
  const modifierLabels = useTaskModifierDisplayNames();
  const wasVisible = useRef(visible);

  useEffect(() => {
    const opened = visible && !wasVisible.current;
    wasVisible.current = visible;
    if (!opened) return;
    setIncludePhotos(true);
    setName(defaultName);
    setParentScanId(defaultBaseArtifactId ?? baseArtifacts.at(-1)?.id ?? '');
    setPhotosOnly(false);
    setResidentResponsibleOnly(false);
    setShowAmounts(true);
  }, [baseArtifacts, defaultBaseArtifactId, defaultName, visible]);

  if (!visible) return null;

  return (
    <ModalShell visible={visible} onClose={onClose} contentMaxWidth='620px'>
      <Card variant='outlined' css={CREATE_MODAL_CARD_STYLE}>
        <div css={EYEBROW_STYLE}>New copy</div>
        <div css={MODAL_TITLE_STYLE}>Create a new audience doc copy</div>
        <div css={SECTION_DESCRIPTION_STYLE}>
          Choose the scope version you want to share, including an active draft, then choose what recipients can see in the copy.
        </div>

        <div css={MODAL_FORM_STYLE}>
          <div css={FIELD_GROUP_STYLE}>
            <CommonFieldShell css={FIELD_SHELL_STYLE}>
              <CommonFieldLabel css={FIELD_LABEL_STYLE}>Name</CommonFieldLabel>
              <CommonFieldInput
                aria-label='Audience doc name'
                css={FIELD_INPUT_STYLE}
                value={name}
                onChange={event => setName(event.target.value)}
                placeholder='e.g. Apex Carpentry - kitchen bid'
              />
            </CommonFieldShell>
          </div>

          <div css={FIELD_GROUP_STYLE}>
            <CommonFieldShell css={FIELD_SHELL_STYLE}>
              <CommonFieldLabel css={FIELD_LABEL_STYLE}>Based on version</CommonFieldLabel>
              <select
                aria-label='Audience doc base version'
                css={FIELD_SELECT_STYLE}
                value={parentScanId}
                onChange={event => setParentScanId(event.target.value)}
              >
                {baseArtifacts.map(baseArtifact => (
                  <option key={baseArtifact.id} value={baseArtifact.id}>
                    {baseArtifactTitlesById.get(baseArtifact.id) ?? baseArtifact.name} · {getHistoricalArtifactSubtitle(baseArtifact)}
                  </option>
                ))}
              </select>
              <CaretDownIcon size={16} css={FIELD_SELECT_ICON_STYLE} />
            </CommonFieldShell>
            <div css={FIELD_HELPER_STYLE}>
              Audience docs are versioned copies, so recipients only see the selected scope snapshot.
            </div>
          </div>

          <div css={FIELD_GROUP_STYLE}>
            {!photosOnly && (
              <AudienceDocSettingToggle
                ariaLabel='Include photos'
                body='Include task photos with each scope item.'
                checked={includePhotos}
                title='Include photos'
                onToggle={() => setIncludePhotos(currentValue => !currentValue)}
              />
            )}
            <AudienceDocSettingToggle
              ariaLabel='Photos only'
              body='Share a gallery of task photos instead of the scope document.'
              checked={photosOnly}
              title='Photos only'
              onToggle={() => setPhotosOnly(currentValue => !currentValue)}
            />
            {!photosOnly && (
              <AudienceDocSettingToggle
                ariaLabel='Show dollar amounts'
                body='Include rates, line-item amounts, and document totals.'
                checked={showAmounts}
                title='Show dollar amounts'
                onToggle={() => setShowAmounts(currentValue => !currentValue)}
              />
            )}
            <AudienceDocSettingToggle
              ariaLabel={`${modifierLabels.tenantResponsibility} only`}
              body={`Only include scope items marked ${modifierLabels.tenantResponsibility}.`}
              checked={residentResponsibleOnly}
              title={`${modifierLabels.tenantResponsibility} only`}
              onToggle={() => setResidentResponsibleOnly(currentValue => !currentValue)}
            />
          </div>
        </div>

        <div css={MODAL_ACTIONS_STYLE}>
          <CommonActionButton
            style={SECONDARY_ACTION_BUTTON_STYLE}
            onClick={onClose}
            disabled={isCreating}
          >
            Cancel
          </CommonActionButton>
          <CommonActionButton
            style={PRIMARY_ACTION_BUTTON_STYLE}
            onClick={() =>
              onCreate({
                includePhotos,
                name,
                parentScanId,
                photosOnly,
                residentResponsibleOnly,
                showAmounts,
              })}
            disabled={isCreating || !name.trim() || !parentScanId}
          >
            {isCreating ? 'Creating...' : 'Create Copy'}
          </CommonActionButton>
        </div>
      </Card>
    </ModalShell>
  );
};
